import { useState, type DragEvent, type ReactNode } from 'react';
import {
  dashboardGridItemClassName,
  resolveCardWidth,
  type DashboardCardWidth
} from './dashboardGridItemClassName';

export interface DashboardSortableGridProps<TId extends string> {
  customizing: boolean;
  visibleOrder: TId[];
  widths?: Partial<Record<TId, DashboardCardWidth>>;
  renderCard: (id: TId) => ReactNode;
  onReorderVisible: (fromIndex: number, toIndex: number) => void;
  onWidthChange?: (id: TId, width: DashboardCardWidth) => void;
  /** When provided, width picker only offers these sizes for the card. */
  getAllowedWidths?: (id: TId) => readonly DashboardCardWidth[] | undefined;
}

interface WidthPickerItem {
  cardWidth: DashboardCardWidth;
  text: string;
  hint: string;
}

const WIDTH_PICKER_ITEMS: WidthPickerItem[] = [
  { cardWidth: 1, text: '1', hint: 'Set card to 1 column' },
  { cardWidth: 2, text: '2', hint: 'Set card to 2 columns' },
  { cardWidth: 3, text: '3', hint: 'Set card to 3 columns' },
  { cardWidth: 'full', text: 'Full', hint: 'Set card to full page width' }
];

/** Plain HTML grid — DX Sortable/ButtonGroup in customize mode can hit React #185 under React 19. */
export function DashboardSortableGrid<TId extends string>({
  customizing,
  visibleOrder,
  widths = {},
  renderCard,
  onReorderVisible,
  onWidthChange,
  getAllowedWidths
}: DashboardSortableGridProps<TId>) {
  const [dragFrom, setDragFrom] = useState<number | null>(null);

  function handleDragStart(index: number, e: DragEvent<HTMLButtonElement>) {
    setDragFrom(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  }

  function handleDrop(toIndex: number, e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const fromIndex = dragFrom ?? Number.parseInt(e.dataTransfer.getData('text/plain'), 10);
    setDragFrom(null);
    if (!Number.isFinite(fromIndex) || fromIndex === toIndex) return;
    onReorderVisible(fromIndex, toIndex);
  }

  return (
    <div
      className={`dashboard-grid${customizing ? ' dashboard-grid--customizing' : ''}`}
    >
      {visibleOrder.map((id, index) => {
        const width = resolveCardWidth(
          id,
          widths[id],
          getAllowedWidths ? (cardId) => getAllowedWidths(cardId as TId) : undefined
        );
        const allowed = getAllowedWidths?.(id);
        const pickerItems =
          allowed && allowed.length > 0
            ? WIDTH_PICKER_ITEMS.filter((item) => allowed.includes(item.cardWidth))
            : WIDTH_PICKER_ITEMS;
        const showWidthPicker = pickerItems.length > 1;

        return (
          <div
            key={id}
            className={`${dashboardGridItemClassName(id, width)}${customizing ? ' dashboard-grid__item--customizing' : ''}`}
            onDragOver={customizing ? (e) => e.preventDefault() : undefined}
            onDrop={customizing ? (e) => handleDrop(index, e) : undefined}
          >
            {customizing ? (
              <div className="dashboard-grid__customize-toolbar">
                <button
                  type="button"
                  className="dashboard-grid__drag-handle"
                  aria-label="Drag to reorder card"
                  draggable
                  onDragStart={(e) => handleDragStart(index, e)}
                  onDragEnd={() => setDragFrom(null)}
                >
                  <span className="dx-icon-dragvertical" aria-hidden />
                  <span className="dashboard-grid__drag-label">Drag to reorder</span>
                </button>
                {showWidthPicker ? (
                  <div className="dashboard-grid__width-picker" role="group" aria-label="Card width">
                    {pickerItems.map((item) => (
                      <button
                        key={String(item.cardWidth)}
                        type="button"
                        className={[
                          'dashboard-grid__width-btn',
                          width === item.cardWidth ? 'dashboard-grid__width-btn--selected' : ''
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        title={item.hint}
                        aria-pressed={width === item.cardWidth}
                        onClick={() => onWidthChange?.(id, item.cardWidth)}
                      >
                        {item.text}
                      </button>
                    ))}
                  </div>
                ) : (
                  <span className="dashboard-grid__drag-label" title="This card is fixed at 1 column">
                    Width locked: 1
                  </span>
                )}
              </div>
            ) : null}
            {renderCard(id)}
          </div>
        );
      })}
    </div>
  );
}
